import React from 'react';
import { Box, Typography, alpha } from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';

const DEFAULT_NEON = '#38BDF8';
const DEFAULT_SURFACE_MID = '#0a1628';
const DEFAULT_SURFACE_UP = '#0f1f38';
const DEFAULT_TEXT_BODY = '#E2E8F0';
const DEFAULT_TEXT_MUTED = '#64748B';

const InfoRow = ({ icon: Icon, label, value, accentColor, textBody, textMuted }) => {
  const display = value || '—';
  const isEmpty = !value;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 0.9,
        px: 1.2,
        borderRadius: '9px',
        transition: 'background 0.15s',
      }}
    >
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: '8px',
          flexShrink: 0,
          background: alpha(accentColor, isEmpty ? 0.05 : 0.1),
          border: `1px solid ${alpha(accentColor, isEmpty ? 0.1 : 0.18)}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon sx={{ fontSize: 14, color: isEmpty ? textMuted : alpha(accentColor, 0.85) }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: '0.65rem',
            color: textMuted,
            fontFamily: "'DM Sans', sans-serif",
            textTransform: 'uppercase',
            letterSpacing: '0.07em',
            fontWeight: 600,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontSize: '0.88rem',
            color: isEmpty ? textMuted : textBody,
            fontStyle: isEmpty ? 'italic' : 'normal',
            fontFamily: "'DM Sans', sans-serif",
            fontWeight: isEmpty ? 400 : 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {display}
        </Typography>
      </Box>
    </Box>
  );
};

const SystemInformationSection = ({
  createdBy,
  createdAt,
  updatedBy,
  updatedAt,
  delay = 0.2,
  accentColor = DEFAULT_NEON,
  surfaceMid = DEFAULT_SURFACE_MID,
  surfaceUp = DEFAULT_SURFACE_UP,
  textBody = DEFAULT_TEXT_BODY,
  textMuted = DEFAULT_TEXT_MUTED,
  createdByLabel = 'Created By',
  createdAtLabel = 'Created At',
  updatedByLabel = 'Updated By',
  updatedAtLabel = 'Updated At',
}) => (
  <Box
    sx={{
      borderRadius: '16px',
      border: `1px solid ${alpha(accentColor, 0.14)}`,
      background: `linear-gradient(160deg, ${alpha(surfaceUp, 0.75)} 0%, ${alpha(surfaceMid, 0.95)} 100%)`,
      overflow: 'hidden',
      animation: `fadeUp 0.4s ease ${delay}s both`,
      '@keyframes fadeUp': {
        from: { opacity: 0, transform: 'translateY(10px)' },
        to: { opacity: 1, transform: 'translateY(0)' },
      },
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.2,
        px: 2.5,
        py: 1.5,
        borderBottom: `1px solid ${alpha(accentColor, 0.1)}`,
        background: alpha(accentColor, 0.04),
      }}
    >
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: '7px',
          background: alpha(accentColor, 0.12),
          border: `1px solid ${alpha(accentColor, 0.22)}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <ScheduleIcon sx={{ fontSize: 13, color: accentColor }} />
      </Box>
      <Typography
        sx={{
          fontFamily: "'DM Sans', sans-serif",
          fontWeight: 700,
          fontSize: '0.78rem',
          color: alpha(textBody, 0.85),
          letterSpacing: '0.07em',
          textTransform: 'uppercase',
        }}
      >
        System Information
      </Typography>
    </Box>
    <Box sx={{ p: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 0.3 }}>
      <InfoRow icon={PersonIcon} label={createdByLabel} value={createdBy} accentColor={accentColor} textBody={textBody} textMuted={textMuted} />
      <InfoRow icon={CalendarIcon} label={createdAtLabel} value={createdAt} accentColor={accentColor} textBody={textBody} textMuted={textMuted} />
      <InfoRow icon={PersonIcon} label={updatedByLabel} value={updatedBy} accentColor={accentColor} textBody={textBody} textMuted={textMuted} />
      <InfoRow icon={TimeIcon} label={updatedAtLabel} value={updatedAt} accentColor={accentColor} textBody={textBody} textMuted={textMuted} />
    </Box>
  </Box>
);

export default SystemInformationSection;
